import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider } from "@/context/ContentContext";
import { ScrollProgress } from "@/components/motion";
import Intro from "@/components/Intro";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Floating from "@/components/Floating";

const Home = lazy(() => import("@/pages/Home"));
const Community = lazy(() => import("@/pages/Community"));
const Brands = lazy(() => import("@/pages/Brands"));
const Music = lazy(() => import("@/pages/Music"));
const Creator = lazy(() => import("@/pages/Creator"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));

gsap.registerPlugin(ScrollTrigger);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => ScrollTrigger.refresh(), 500);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function Site({ revealed }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar revealed={revealed} />}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -18, transition: { duration: 0.35, ease: [0.4, 0, 1, 1] } }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Suspense fallback={<div className="min-h-screen bg-ink" />}>
          <Routes location={location}>
            <Route path="/" element={<Home revealed={revealed} />} />
            <Route path="/community" element={<Community entryReady={revealed} />} />
            <Route path="/brands" element={<Brands entryReady={revealed} />} />
            <Route path="/music" element={<Music entryReady={revealed} />} />
            <Route path="/creator" element={<Creator entryReady={revealed} />} />
            <Route path="/about" element={<About entryReady={revealed} />} />
            <Route path="/contact" element={<Contact entryReady={revealed} />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
          </Suspense>
        </motion.main>
      </AnimatePresence>
      {!isAdmin && <Footer />}
      {!isAdmin && <Floating revealed={revealed} />}
    </>
  );
}

function App() {
  const [introSeen] = useState(() => !!sessionStorage.getItem("mp_intro_seen"));
  const [introDone, setIntroDone] = useState(introSeen);
  const [revealed, setRevealed] = useState(introSeen);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.17, wheelMultiplier: 1.15 });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      if (window.__lenis === lenis) window.__lenis = undefined;
    };
  }, []);

  const finishIntro = useCallback(() => {
    sessionStorage.setItem("mp_intro_seen", "1");
    setIntroDone(true);
    setTimeout(() => setRevealed(true), 200);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <ContentProvider>
      <BrowserRouter>
        <div className="grain-overlay" />
        <ScrollProgress />
        {!introDone && <Intro onDone={finishIntro} />}
        <Site revealed={revealed} />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
      </ContentProvider>
    </MotionConfig>
  );
}

export default App;
